import {
    RealRewardStep,
    type ContactInfo as SDKContactInfo,
    type RewardInfo,
} from "@hg/activity-utils"
import Modal from "@hg/modal"
import { useEffect, useState } from "react"

import {
    getDrawInfo,
    postDraw,
    postDrawContact,
    type ContactInfo,
} from "@/services/core"
import Toast from "@/components/Toast"
import { collectLottery } from "@/utils/event"
import { MAIN_REWARD_MAP } from "./const"
import { Failed, RealReward, Rule, VirtualReward } from "./UI"

type Stage = "rule" | "virtual" | "real" | "miss"

interface Props {
    open: boolean
    onClose: () => void
}

const getRewardInfo = (giftName: string): RewardInfo | null => {
    const r = MAIN_REWARD_MAP[giftName]
    return r ? (r as unknown as RewardInfo) : null
}

export const MainLotteryModal: React.FC<Props> = ({ open, onClose }) => {
    const [stage, setStage] = useState<Stage | null>(null)
    const [drawing, setDrawing] = useState(false)
    const [reward, setReward] = useState<RewardInfo | null>(null)

    const [step, setStep] = useState<RealRewardStep>(RealRewardStep.EDIT)
    const [contact, setContact] = useState<ContactInfo>({
        name: "",
        phone: "",
        email: "",
        postcode: "",
        address: "",
        province: "",
        city: "",
        county: "",
    })
    const [contactLoading, setContactLoading] = useState(false)

    useEffect(() => {
        if (!open) {
            setStage(null)
            return
        }
        setDrawing(false)

        getDrawInfo().then((res) => {
            if (res.code !== 0) {
                setStage("rule")
                return
            }

            const mainCoupons = res.data.coupons.filter(
                (c) => c.cid === __MAIN_SHARE_COUPON_CID,
            )
            const remaining =
                res.data.availableDrawCountMap[__MAIN_SHARE_COUPON_CID] ?? 0
            const contactInfo = res.data.contactInfo?.[__MAIN_SHARE_COUPON_CID]

            const wonGoods = mainCoupons.find(
                (c) => c.result?.prizeType === "GOODS" && c.result?.won,
            )
            const wonGift = mainCoupons.find(
                (c) => c.result?.prizeType === "GIFT" && c.result?.won,
            )

            if (wonGoods) {
                setReward(getRewardInfo(wonGoods.result!.giftName))
                if (contactInfo) {
                    setContact(contactInfo as ContactInfo)
                    setStep(RealRewardStep.DONE)
                } else {
                    setStep(RealRewardStep.EDIT)
                }
                setStage("real")
            } else if (remaining === 0 && wonGift) {
                setReward(getRewardInfo(wonGift.result!.giftName))
                setStage("virtual")
            } else {
                setReward(null)
                setStage("rule")
            }
        })
    }, [open])

    const handleDraw = async () => {
        if (drawing) return
        setDrawing(true)
        try {
            const res = await postDraw(__MAIN_SHARE_COUPON_CID)
            if (res.code === 0) {
                if (res.data.prizeType === "MISS") {
                    setStage("miss")
                    return
                }
                setReward(getRewardInfo(res.data.giftName))
                collectLottery("game", res.data.prizeType === "GIFT")
                setStage(res.data.prizeType === "GOODS" ? "real" : "virtual")
            } else {
                Toast.message(res.msg || "draw failed")
            }
        } finally {
            setDrawing(false)
        }
    }

    const handleSubmitContact = async () => {
        setContactLoading(true)
        try {
            const res = await postDrawContact(__MAIN_SHARE_COUPON_CID, contact)
            if (res.code === 0) {
                setStep(RealRewardStep.DONE)
            } else {
                Toast.message(res.msg || "-")
            }
        } catch (error: any) {
            Toast.message(error.message || "-")
        } finally {
            setContactLoading(false)
        }
    }

    return (
        <Modal active={open} onClose={onClose}>
            {stage === "rule" ? (
                <Rule onConfirm={handleDraw} onClose={onClose} loading={drawing} />
            ) : null}
            {stage === "virtual" && reward ? (
                <VirtualReward reward={reward} onClose={onClose} />
            ) : null}
            {stage === "real" && reward ? (
                <RealReward
                    reward={reward}
                    onClose={onClose}
                    step={step}
                    loading={contactLoading}
                    contact={contact as unknown as SDKContactInfo}
                    onContactChange={(c) =>
                        setContact(c as unknown as ContactInfo)
                    }
                    onSubmitContact={handleSubmitContact}
                    onStepChange={setStep}
                />
            ) : null}
            {stage === "miss" ? (
                <Failed onClose={onClose} type="noReward" />
            ) : null}
        </Modal>
    )
}
